/* Discount Bar — v23.7 Geometry-Based Half-Step + Free Counter
   - Any number of .step nodes (incl. "No limit")
   - Progress caps at 100% when items >= steps*3
   - NEW: counts zero-price items and updates #zero-price-count
*/

(function () {
  "use strict";

  const BAR_ID = "cart-milestones-bar";
  const PROGRESS_ID = "progress-bar";
  const FREE_CNT_ID = "zero-price-count";

  const $ = (s) => document.querySelector(s);
  const $$ = (s) => Array.from(document.querySelectorAll(s));

  /* ---------- Fetch cart ---------- */
  async function fetchCart() {
    try {
      const r = await fetch("/cart.js", { credentials: "same-origin" });
      return r.ok ? r.json() : null;
    } catch {
      return null;
    }
  }

  /* ---------- Count items ---------- */
  async function buildState(cart) {
    let total = 0;
    for (const it of cart.items || []) total += Number(it.quantity) || 0;
    return { totalItems: total };
  }

  /* ---------- Helpers: money/discount parsing ---------- */
  const toCents = (v) => {
    if (typeof v === "number") return v;
    if (typeof v === "string") return Math.round(parseFloat(v) * 100);
    return 0;
  };

  function sumDiscountAllocationsCents(item) {
    let sum = 0;

    // Modern: line_level_discount_allocations: [{ amount: "1.23" | 123, ... }]
    if (Array.isArray(item?.line_level_discount_allocations)) {
      for (const a of item.line_level_discount_allocations) {
        sum += toCents(a?.amount ?? a?.amount_set?.shop_money?.amount);
      }
    }

    // Sometimes: discount_allocations: [{ amount: 123 | "1.23", ... }]
    if (Array.isArray(item?.discount_allocations)) {
      for (const a of item.discount_allocations) {
        sum += toCents(a?.amount ?? a?.amount_set?.shop_money?.amount);
      }
    }

    // Very old: discounts: [{ amount: "1.23" | 123 }]
    if (Array.isArray(item?.discounts)) {
      for (const d of item.discounts) {
        sum += toCents(d?.amount);
      }
    }

    return sum;
  }

  function unitFinalPriceCents(item) {
    // Prefer explicit finals if present
    if (typeof item?.final_price === "number") return item.final_price;
    if (typeof item?.final_line_price === "number" && item.quantity) {
      return Math.max(0, Math.round(item.final_line_price / item.quantity));
    }

    // Derive from line price - discounts
    const qty = Math.max(1, Number(item?.quantity) || 1);
    const baseLine =
      typeof item?.line_price === "number"
        ? item.line_price
        : (Number(item?.price) || 0) * qty;

    const discounts = sumDiscountAllocationsCents(item);
    const finalLine = Math.max(0, baseLine - discounts);
    return Math.round(finalLine / qty);
  }

  function computeZeroPriceCount(cart) {
    let count = 0;
    for (const it of cart.items || []) {
      const perUnit = unitFinalPriceCents(it);
      if (perUnit === 0) count += Number(it.quantity) || 0;
    }
    return count;
  }

  /* ---------- Geometry anchors ---------- */
  function getStepAnchors() {
    const steps = $$("#" + BAR_ID + " .step");
    if (!steps.length) return [];
    const bar = $("#" + BAR_ID + " .progress-bar");
    if (!bar) return [];

    const barRect = bar.getBoundingClientRect();
    return steps.map((step) => {
      const rect = step.getBoundingClientRect();
      const cx = (rect.left + rect.right) / 2;
      return ((cx - barRect.left) / barRect.width) * 100;
    });
  }

  /* ---------- Compute fill ---------- */
  function computeProgress(totalItems) {
    const anchors = getStepAnchors();
    if (!anchors.length || totalItems <= 0) return 0;

    const stepsCount = anchors.length;
    const maxItems = stepsCount * 3;                // 3 items per step
    const current = Math.min(totalItems, maxItems);

    const stage = Math.floor((current - 1) / 3);    // 0-based
    const mod = (current - 1) % 3;                  // 0=half, 1/2=full

    const start = stage === 0 ? 0 : anchors[stage - 1];
    const end = anchors[stage] ?? 100;

    let pct = mod === 0 ? start + (end - start) * 0.5 : end;

    if (totalItems >= maxItems) pct = 100;          // clamp at end
    return Math.min(Math.max(pct, 0), 100);
  }

  /* ---------- Paint visuals ---------- */
  function paintProgress(pct) {
    const el = $("#" + PROGRESS_ID);
    if (el) el.style.width = `${pct}%`;
  }

  function paintStepsByFreeCount(freeCount) {
    const steps = $$("#" + BAR_ID + " .step");
    steps.forEach((step) => {
      const stepIndex = Number(step.dataset.step) || 0; // 1-based
      const completed = freeCount >= stepIndex;
      step.classList.toggle("completed", completed);
    });
  }

  function paintFreeCount(n) {
    const el = $("#" + FREE_CNT_ID);
    if (!el) return;
    el.textContent = String(n);
    // Optional: hide/show wrapper if you prefer
    // el.closest(".free-products-bar__message")?.classList.toggle("is-hidden", n === 0);
  }

  /* ---------- Refresh ---------- */
  async function refresh() {
    const cart = await fetchCart();
    if (!cart) return;

    const { totalItems } = await buildState(cart);
    const pct = computeProgress(totalItems);
    const freeCount = computeZeroPriceCount(cart);

    paintProgress(pct);
    paintStepsByFreeCount(freeCount);
    paintFreeCount(freeCount);
  }

  /* ---------- Lifecycle ---------- */
  function wire() {
    refresh();

    const events = [
      "cart:change",
      "cart:updated",
      "cart:refresh",
      "cart-drawer:refreshed",
      "theme:cart:update",
    ];
    events.forEach((e) => document.addEventListener(e, refresh, { passive: true }));

    document.addEventListener("change", (e) => {
      if (e.target.matches?.(".quantity-selector__input")) setTimeout(refresh, 300);
    });
    document.addEventListener("click", (e) => {
      if (e.target.closest?.(".quantity-selector__button")) setTimeout(refresh, 300);
    });

    window.addEventListener("pageshow", refresh);
    window.addEventListener("resize", refresh);
  }

  document.addEventListener("DOMContentLoaded", wire);
})();